import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { setLodgeAIError, closeLodgeAIModal, resetLodgeAI } from "@/store/slices/lodgeAISlice";
import { useLodgeAIContext } from "@/providers/LodgeAIContextProvider";

const useLodgeAIAction = () => {
  const dispatch = useDispatch();
  const { sdk, sessionActions } = useLodgeAIContext();

  // Early return if SDK is not ready.
  if (!sdk || !sessionActions) {
    return {
      startAutomation: () => undefined,
      enableManualControl: () => undefined,
      resumeAutomation: () => undefined,
      skipCurrentStep: () => undefined,
      stopSession: () => undefined,
    };
  }

  // Stop LodgeAI session.
  const stopSession = useCallback(async () => {
    try {
      await sessionActions.disconnect();
    } catch (err) {
      // Do nothing.
    } finally {
      // Reset LodgeAI.
      dispatch(resetLodgeAI());
      // Close LodgeAI modal.
      dispatch(closeLodgeAIModal());
    }
  }, [sessionActions]);

  // Start LodgeAI automation.
  const startAutomation = useCallback(async () => {
    try {
      await sessionActions.startAutomation();
    } catch (err) {
      dispatch(
        setLodgeAIError({
          title: "Failed to start automation",
          message: err instanceof Error ? err.message : "Unknown error",
        }),
      );
    }
  }, [sessionActions]);

  // Enable manual control of the automation.
  const enableManualControl = useCallback(async () => {
    try {
      await sessionActions.enableManualControl();
    } catch (err) {
      dispatch(
        setLodgeAIError({
          title: "Failed to enable manual control",
          message: err instanceof Error ? err.message : "Unknown error",
        }),
      );
    }
  }, [sessionActions]);

  // Resume automation.
  const resumeAutomation = useCallback(async () => {
    try {
      await sessionActions.resumeAutomation();
    } catch (err) {
      dispatch(
        setLodgeAIError({
          title: "Failed to resume automation",
          message: err instanceof Error ? err.message : "Unknown error",
        }),
      );
    }
  }, [sessionActions]);

  // Skip current step.
  const skipCurrentStep = useCallback(async () => {
    try {
      await sessionActions.skipCurrentStep();
    } catch (err) {
      dispatch(
        setLodgeAIError({
          title: "Failed to skip step",
          message: err instanceof Error ? err.message : "Unknown error",
        }),
      );
    }
  }, [sessionActions]);

  return {
    stopSession,
    startAutomation,
    enableManualControl,
    resumeAutomation,
    skipCurrentStep,
  };
};

export { useLodgeAIAction };
